import { collection, CollectionReference, Firestore, FirestoreError, getDocs, onSnapshot, Query, QuerySnapshot } from 'firebase/firestore';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

let _db: Firestore | null = null;

export const initFirestore = (db: Firestore): void => {
    _db = db;
};

const getDb = (): Firestore => {
    if (!_db) throw new Error('Firestore not initialized — call initFirestore(app) first');
    return _db;
};

export interface FirestoreStreamConfig<TResult, TArg> {
    reducerPath: string;
    /**
     * Ref to the collection/query that will be streamed with onSnapshot.
     */
    getRef: (db: Firestore, arg: TArg) => CollectionReference | Query;
    /**
     * Optional second collection read once at subscription time (for defaults / seed data).
     */
    getDefaultsRef?: (db: Firestore, arg: TArg) => CollectionReference | Query;
    /**
     * Map the live snapshot (and optional defaults snapshot) to the desired result type.
     * Called on every snapshot update.
     */
    mapData: (snapshot: QuerySnapshot, defaultsSnapshot: QuerySnapshot | null) => TResult;
    /**
     * @deprecated no longer read. queryFn now awaits the real first onSnapshot callback (or its
     * error) instead of resolving instantly with a placeholder, so existing callers don't need
     * to change -- kept only so their config object literals don't need editing.
     */
    emptyResult: () => TResult;
}

interface PendingFirstSnapshot<TResult> {
    promise: Promise<TResult>;
    resolve: (result: TResult) => void;
    reject: (error: unknown) => void;
}

/**
 * Creates an RTK Query API backed by a Firestore onSnapshot listener.
 *
 * Usage:
 *   export const myApi = createFirestoreStreamApi({ reducerPath: 'myApi', ... });
 *   export const { useGetQuery } = myApi;
 *
 * Register myApi in the Redux store (reducer + middleware) the same way as any
 * other RTK Query API, and call initFirestore(db) once after Firebase initialises.
 */
export function createFirestoreStreamApi<TResult, TArg>(
    config: FirestoreStreamConfig<TResult, TArg>,
) {
    // Bridges queryFn (which RTK Query reads to derive isLoading/isSuccess/error) with the one
    // onSnapshot listener onCacheEntryAdded owns below, so isLoading stays true until the real
    // first snapshot (or its error) arrives instead of an instant placeholder. Both queryFn and
    // onCacheEntryAdded call getOrCreatePending() for the same arg -- whichever of the two RTK
    // Query happens to invoke first creates the entry, so there's no dependency on their
    // relative ordering, and neither needs its own separate onSnapshot/getDocs call (which would
    // double Firestore reads on every first load).
    const pending = new Map<string, PendingFirstSnapshot<TResult>>();
    const keyOf = (arg: TArg): string => JSON.stringify(arg ?? null);
    const getOrCreatePending = (key: string): PendingFirstSnapshot<TResult> => {
        let entry = pending.get(key);
        if (!entry) {
            let resolve!: (result: TResult) => void;
            let reject!: (error: unknown) => void;
            const promise = new Promise<TResult>((res, rej) => {
                resolve = res;
                reject = rej;
            });
            entry = { promise, resolve, reject };
            pending.set(key, entry);
        }
        return entry;
    };

    return createApi({
        reducerPath: config.reducerPath,
        baseQuery: fakeBaseQuery(),
        endpoints: (build) => ({
            get: build.query<TResult, TArg>({
                queryFn: async (arg) => {
                    try {
                        return { data: await getOrCreatePending(keyOf(arg)).promise };
                    } catch (error) {
                        return {
                            error: {
                                status: 'FIRESTORE_ERROR',
                                error: error instanceof Error ? error.message : String(error),
                            },
                        };
                    }
                },

                async onCacheEntryAdded(arg, { updateCachedData, cacheEntryRemoved }) {
                    const key = keyOf(arg);
                    const { resolve, reject } = getOrCreatePending(key);
                    let unsubscribe: () => void = () => {};
                    let firstSnapshot = true;

                    // Settles queryFn's promise (success or failure) exactly once, from whichever
                    // of the callbacks below fires first, and drops the map entry once settled.
                    const settleFirst = (settle: () => void) => {
                        if (firstSnapshot) {
                            firstSnapshot = false;
                            settle();
                            pending.delete(key);
                        }
                    };

                    try {
                        const db = getDb();

                        let defaultsSnapshot: QuerySnapshot | null = null;
                        if (config.getDefaultsRef) {
                            defaultsSnapshot = await getDocs(
                                config.getDefaultsRef(db, arg) as CollectionReference,
                            );
                        }

                        unsubscribe = onSnapshot(
                            config.getRef(db, arg) as Query,
                            (snapshot) => {
                                const data = config.mapData(snapshot, defaultsSnapshot);
                                if (firstSnapshot) {
                                    settleFirst(() => resolve(data));
                                } else {
                                    updateCachedData(() => data);
                                }
                            },
                            (error) => {
                                console.error(`[${config.reducerPath}] Firestore snapshot error:`, error);
                                // A later error (listener drops after already loading) has no RTK
                                // Query error channel to report through here -- last-known data
                                // stays cached, same as before this change.
                                settleFirst(() => reject(error));
                            },
                        );
                    } catch (e) {
                        console.error(`[${config.reducerPath}] Failed to start Firestore listener:`, e);
                        settleFirst(() => reject(e));
                    }

                    await cacheEntryRemoved;
                    pending.delete(key);
                    unsubscribe();
                },
            }),
        }),
    });
}

// Test-only hook, mirroring firebaseInstance.ts's window.__firebaseTestHelpers pattern: lets a
// Playwright test attempt a raw Firestore read as whichever user is currently signed in, to
// verify security rules directly rather than through this module's own streaming APIs (e.g.
// specs/015-adopt-my-reservations-view tasks.md T006a -- confirming a non-admin genuinely gets
// permission-denied reading another user's data).
declare global {
    interface Window {
        __firestoreTestHelpers?: {
            attemptReadCollection: (path: string) => Promise<{ ok: boolean; errorCode?: string }>;
        };
    }
}
window.__firestoreTestHelpers = {
    attemptReadCollection: async (path: string) => {
        try {
            await getDocs(collection(getDb(), path));
            return { ok: true };
        } catch (e) {
            return { ok: false, errorCode: (e as FirestoreError).code };
        }
    },
};
